import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { keyBy } from 'lodash';
import { useClient } from 'urql';

import useEvent from '@lyearn/core/hooks/useEvent';
import useToggle from '@lyearn/core/hooks/useToggle';
import ErrorReporter from '@lyearn/core/utils/ErrorReporter';
import { jsonParse } from '@lyearn/core/utils/json';
import { ReactExtensionComponentProps } from '@lyearn/lygo-core';
import { useSnackbar } from '@lyearn/ui';
import AsyncView from '@/components/AsyncView';
import { GenerativeTaskFragment } from '@/components/LyearnAI/graphql/fragments/generated/GenerativeTaskFragment';
import { useGenerativeTaskQuery } from '@/components/LyearnAI/graphql/queries/generated/generativeTaskQuery';
import fetchQuery from '@/helper/data-op/fetchQuery';
import { createResilientGenerativeTaskStream } from '@/hooks/useResilientGenerativeTask/resilientGenerativeTaskStream';
import { GenerativeTaskStatusEnum } from '@/types/schema';

import {
  VideoSlideTemplatesDocument,
  VideoSlideTemplatesQuery,
  VideoSlideTemplatesQueryVariables,
} from './graphql/queries/generated/videoSlideTemplates';
import { StoryboardSlide, VideoSlideTemplate } from './components';
import GeneratedVideo from './GeneratedVideo';
import GenerateVideoBanner, { GenerateVideoBannerState } from './GenerateVideoBanner';
import { GenerateVideoFormValues } from './GenerateVideoFrom';
import GenerateVideoModal from './GenerateVideoModal';
import GeneratingVideo from './GeneratingVideo';
import StoryboardModal from './StoryboardModal';
import {
  SwitchVideoSlideTemplateContext,
  useSwitchVideoSlideTemplate,
} from './useSwitchVideoSlideTemplate';
import { useVideoGeneration } from './useVideoGeneration';
import { useVideoScriptGeneration } from './useVideoScriptGeneration';
import { useVideoSlideSelection, VideoSlideFromSelection } from './useVideoSlideSelection';
import { SLIDE_ITEM_CHAR_LIMIT, SLIDE_VAR_CHAR_LIMITS } from './videoSlideConstants';

type VideoSlideTemplateNode = NonNullable<
  NonNullable<VideoSlideTemplatesQuery['videoSlideTemplates']['edges']>[number]
>['node'];

function getSchemaKeys(variableSchema?: string | null): string[] | null {
  if (!variableSchema) {
    return null;
  }
  try {
    const schema = jsonParse(variableSchema).data as Record<string, unknown>;
    return Object.keys(schema ?? {});
  } catch {
    return null;
  }
}

function getDefaultVariablesFromSchema(variableSchema?: string | null): Record<string, string> {
  const keys = getSchemaKeys(variableSchema);
  if (!keys) {
    return {};
  }
  return Object.fromEntries(keys.map((key) => [key, '']));
}

function createStoryboardSlideFromTemplate(
  template: VideoSlideTemplateNode,
  input?: {
    transcript?: string;
    variables?: Record<string, string>;
  },
): StoryboardSlide {
  const schemaKeys = getSchemaKeys(template.variableSchema);
  const variables = schemaKeys
    ? Object.fromEntries(schemaKeys.map((key) => [key, input?.variables?.[key] ?? '']))
    : input?.variables ?? {};

  return {
    slideTemplateId: template.id,
    heygenTemplateId: template.heygen_template_id,
    name: template.name,
    transcript: input?.transcript ?? '',
    variables,
    previewUrl: template.preview_url ?? undefined,
    variableSchema: template.variableSchema ?? undefined,
  };
}

function formatVideoDuration(totalSeconds: number): string | null {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return null;
  const rounded = Math.round(totalSeconds);
  const seconds = rounded % 60;
  const minutesTotal = Math.floor(rounded / 60);
  const minutes = minutesTotal % 60;
  const hours = Math.floor(minutesTotal / 60);

  const ss = String(seconds).padStart(2, '0');
  if (hours > 0) {
    const mm = String(minutes).padStart(2, '0');
    return `${hours}:${mm}:${ss} hr`;
  }

  return `${minutesTotal}:${ss} min`;
}

const GenerateVideo = (props: ReactExtensionComponentProps) => {
  const { editorView, editable, attrs, updateAttribute } = props;
  const { showErrorSnackbar } = useSnackbar();

  const [modalState, setModalState] = useState<'script' | 'storyboard' | null>(null);
  const [isModalOpen, { on: openModal, off: closeModal }] = useToggle();
  const [shouldAutoOpenScriptPrompt, setShouldAutoOpenScriptPrompt] = useState(false);
  const hasAutoOpenedRef = React.useRef(false);
  const openScriptModal = useCallback(
    (autoOpenPrompt = false) => {
      setShouldAutoOpenScriptPrompt(autoOpenPrompt);
      setModalState('script');
      openModal();
    },
    [openModal],
  );
  const openStoryboardModal = useCallback(() => {
    setModalState('storyboard');
    openModal();
  }, [openModal]);
  const isScriptModalOpen = isModalOpen && modalState === 'script';
  const isStoryboardModalOpen = isModalOpen && modalState === 'storyboard';

  const videoScript = attrs?.videoScript ?? '';
  const onChangeVideoScript = (script: string) => {
    updateAttribute({ videoScript: script });
  };

  const slideDeckUrl = (attrs?.slideDeckUrl ?? '') as string;
  const slideDeckFileName = (attrs?.slideDeckFileName ?? '') as string;
  const slideDeckFileSummary = (attrs?.slideDeckFileSummary ?? '') as string;
  const videoAvatarId = (attrs?.avatarId ?? '') as string;
  const videoVoiceId = (attrs?.voiceId ?? '') as string;
  const videoType = (attrs?.videoType ?? 'avatarNarration') as GenerateVideoFormValues['videoType'];
  const onChangeVideoConfig = ({
    avatarId,
    voiceId,
    videoType: nextVideoType,
  }: {
    avatarId?: string;
    voiceId?: string;
    videoType?: GenerateVideoFormValues['videoType'];
  }) => {
    updateAttribute({ avatarId, voiceId, videoType: nextVideoType });
  };

  const storyboardSlides = useMemo(
    () => jsonParse<StoryboardSlide[]>((attrs?.slides as string) || '[]').data || [],
    [attrs?.slides],
  );
  const onChangeStoryboardSlides = useCallback(
    (slides: StoryboardSlide[]) => {
      updateAttribute({ slides: JSON.stringify(slides) });
    },
    [updateAttribute],
  );
  const [isPreparingStoryboard, setIsPreparingStoryboard] = useState(false);
  const [selectedStoryboardSlideIndex, setSelectedStoryboardSlideIndex] = useState(0);
  const [switchingStoryboardSlideIndex, setSwitchingStoryboardSlideIndex] = useState<number | null>(
    null,
  );
  const client = useClient();

  const fetchVideoSlideTemplatesByIds = useCallback(
    async (videoSlideTemplateIds: string[]) => {
      const uniqueTemplateIds = Array.from(new Set(videoSlideTemplateIds.filter(Boolean)));
      if (uniqueTemplateIds.length === 0) {
        return [];
      }

      const { error, data: templatesData } = await fetchQuery<VideoSlideTemplatesQuery>(
        client,
        VideoSlideTemplatesDocument,
        {
          filters: {
            videoSlideTemplateIds: uniqueTemplateIds,
          },
        } satisfies VideoSlideTemplatesQueryVariables,
        {},
      );

      if (error) {
        showErrorSnackbar({
          message: 'Failed to load video slide templates. Please try again.',
        });
        return null;
      }

      return templatesData?.videoSlideTemplates?.edges?.map((edge) => edge.node) ?? [];
    },
    [client, showErrorSnackbar],
  );

  const handleVideoScriptGenerationSuccess = useEvent(
    ({ script, fileSummary }: { script: string; fileSummary?: string | null }) => {
      updateAttribute({
        videoScript: script,
        slideDeckFileSummary: slideDeckUrl ? fileSummary ?? null : null,
      });
    },
  );

  const {
    generateVideoScript,
    regenerateVideoScript,
    getLessonContent,
    generatedVideoScript,
    isGeneratingVideoScript,
  } = useVideoScriptGeneration({
    editorView,
    onSuccess: handleVideoScriptGenerationSuccess,
    prevVideoScript: videoScript,
    slideDeckFilePath: slideDeckUrl,
  });

  const handleSlideSelectionSuccess = useEvent(async (slides: VideoSlideFromSelection[]) => {
    setIsPreparingStoryboard(true);

    if (!slides || slides.length === 0) {
      setIsPreparingStoryboard(false);
      return;
    }

    const templates = await fetchVideoSlideTemplatesByIds(
      slides.map((slide) => slide.slideTemplateId),
    );
    if (!templates) {
      setIsPreparingStoryboard(false);
      return;
    }
    const templateMap = keyBy(templates, (t) => t.id);

    const newStoryboardSlides: StoryboardSlide[] = slides
      .map((slide) => {
        const matchedTemplate = templateMap[slide.slideTemplateId];
        if (!matchedTemplate) {
          return null;
        }

        return createStoryboardSlideFromTemplate(matchedTemplate, {
          transcript: slide.transcript,
          variables: slide.variables,
        });
      })
      .filter(Boolean) as StoryboardSlide[];

    newStoryboardSlides.forEach((slide, slideIdx) => {
      (Object.entries(slide.variables ?? {}) as Array<[string, string]>).forEach(([key, value]) => {
        const limit = /^item\d+$/.test(key) ? SLIDE_ITEM_CHAR_LIMIT : SLIDE_VAR_CHAR_LIMITS[key];
        if (limit !== undefined && value.length > limit) {
          ErrorReporter.error(
            new Error(
              `AI video slide variable "${key}" exceeds ${limit} char limit (got ${
                value.length
              }) on slide ${slideIdx + 1}`,
            ),
          );
        }
      });
    });

    onChangeStoryboardSlides(newStoryboardSlides);
    setSelectedStoryboardSlideIndex(0);
    setIsPreparingStoryboard(false);
  });

  const { selectSlides, isSelectingSlides } = useVideoSlideSelection({
    onSuccess: handleSlideSelectionSuccess,
  });

  const handleSwitchVideoSlideTemplateSuccess = useEvent(
    async (slide: VideoSlideFromSelection, context: SwitchVideoSlideTemplateContext) => {
      if (!storyboardSlides[context.slideIndex]) {
        return;
      }

      const matchedTemplate = context.template;
      if (!matchedTemplate) {
        return;
      }

      const updatedSlide = createStoryboardSlideFromTemplate(matchedTemplate, {
        transcript: slide.transcript,
        variables: slide.variables ?? {},
      });

      onChangeStoryboardSlides(
        storyboardSlides.map((currentSlide, index) =>
          index === context.slideIndex ? updatedSlide : currentSlide,
        ),
      );
    },
  );

  const { switchVideoSlideTemplate, isSwitchingVideoSlideTemplate } = useSwitchVideoSlideTemplate({
    onSuccess: handleSwitchVideoSlideTemplateSuccess,
    onError: () => {
      setSwitchingStoryboardSlideIndex(null);
    },
  });

  useEffect(() => {
    if (!isSwitchingVideoSlideTemplate) {
      setSwitchingStoryboardSlideIndex(null);
    }
  }, [isSwitchingVideoSlideTemplate]);

  const handleGenerateStoryboard = useEvent(() => {
    openStoryboardModal();
    selectSlides({
      videoScript,
      lessonContent: getLessonContent(),
      filePath: slideDeckUrl || undefined,
      fileSummary: slideDeckFileSummary || undefined,
    });
  });

  const hasPreviouslyGeneratedVideo = Boolean(attrs?.generativeTaskId);

  const setGenerativeTaskIdForRendering = useCallback(
    (gtId: string) => {
      updateAttribute({ generativeTaskId: gtId });
    },
    [updateAttribute],
  );

  const [resumedGenerativeTask, setResumedGenerativeTask] = useState<GenerativeTaskFragment | null>(
    null,
  );
  const currentRenderedVideoUrlRef = React.useRef<string | null>(null);
  const [renderStartVideoUrl, setRenderStartVideoUrl] = useState<string | null>(null);

  const handleRenderVideoStart = useCallback(
    (gtId: string) => {
      closeModal();
      setRenderStartVideoUrl(currentRenderedVideoUrlRef.current);
      setGenerativeTaskIdForRendering(gtId);
    },
    [closeModal, setGenerativeTaskIdForRendering],
  );

  const {
    generateVideo,
    isCreatingVideo,
    isRenderingVideo,
    generatedVideoUrl,
    generatedCaptionUrl,
    completedVideosCount,
    totalVideosCount,
    videoGenerationProgress,
  } = useVideoGeneration({
    onRenderStart: handleRenderVideoStart,
  });

  const [{ data: initialGenerativeTaskData, fetching: isFetchingInitialGenerativeTask }] =
    useGenerativeTaskQuery({
      pause: !hasPreviouslyGeneratedVideo || !editable,
      variables: { filters: { generativeTaskId: attrs?.generativeTaskId || '' } },
    });

  const initialGenerativeTask = initialGenerativeTaskData?.generativeTask?.generativeTask;
  const initialGenerativeTaskId = initialGenerativeTask?.id;
  const initialGenerativeTaskStatus = initialGenerativeTask?.status;

  const resumedStreamTaskIdRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (!initialGenerativeTaskId || !initialGenerativeTaskStatus) return;

    const isStillProcessing = [
      GenerativeTaskStatusEnum.Processing,
      GenerativeTaskStatusEnum.ToBeProcessed,
    ].includes(initialGenerativeTaskStatus);

    if (!isStillProcessing) return;

    // urql may update `initialGenerativeTask` reference frequently (cache updates).
    // Guard to avoid re-creating the stream for the same task id.
    if (resumedStreamTaskIdRef.current === initialGenerativeTaskId) {
      return;
    }
    resumedStreamTaskIdRef.current = initialGenerativeTaskId;

    const { subscribe } = createResilientGenerativeTaskStream(client, initialGenerativeTaskId, {
      globalTimeoutMs: 3600_000,
      subscriptionTimeoutMs: 60_000,
      pollingStartDelayMs: 0,
      pollingIntervalMs: 30_000,
      pollingMaxAttempts: 120,
      onData: ({ generativeTask }) => {
        setResumedGenerativeTask(generativeTask);
      },
      onError: () => {
        setResumedGenerativeTask(null);
      },
    });

    const unsubscribe = subscribe();
    return unsubscribe;
  }, [client, initialGenerativeTaskId, initialGenerativeTaskStatus]);

  const effectiveGenerativeTask = resumedGenerativeTask || initialGenerativeTask || null;
  const effectiveGenerativeTaskVideoGeneration = effectiveGenerativeTask?.result?.videoGeneration;
  const resumedVideoUrl =
    effectiveGenerativeTaskVideoGeneration?.videoPlaylist?.videoUrl ||
    effectiveGenerativeTaskVideoGeneration?.videoUrl;
  const resumedCaptionUrl = effectiveGenerativeTaskVideoGeneration?.videoPlaylist?.captionUrl;
  const resumedTotalCount = effectiveGenerativeTaskVideoGeneration?.videos?.length ?? 0;
  const resumedCompletedCount =
    effectiveGenerativeTaskVideoGeneration?.videos?.filter(
      (v: { status: string }) => v.status === 'completed',
    ).length ?? 0;
  const resumedVideoGenerationProgress = effectiveGenerativeTask?.progress ?? null;
  const isResumedRendering =
    !resumedVideoUrl &&
    !!resumedGenerativeTask &&
    [GenerativeTaskStatusEnum.Processing, GenerativeTaskStatusEnum.ToBeProcessed].includes(
      resumedGenerativeTask.status,
    );

  const effectiveVideoUrl = generatedVideoUrl || resumedVideoUrl;
  const effectiveCaptionUrl = generatedCaptionUrl || resumedCaptionUrl;
  const effectiveCompletedCount = isRenderingVideo ? completedVideosCount : resumedCompletedCount;
  const effectiveTotalCount = isRenderingVideo ? totalVideosCount : resumedTotalCount;
  const effectiveVideoGenerationProgress = isRenderingVideo
    ? videoGenerationProgress
    : resumedVideoGenerationProgress;
  const fallbackVideoUrl = attrs?.src || renderStartVideoUrl || null;
  const fallbackCaptionUrl = attrs?.captions || null;
  const displayedVideoUrl = effectiveVideoUrl || fallbackVideoUrl;
  const displayedCaptionUrl = effectiveCaptionUrl || fallbackCaptionUrl;
  const [videoDuration, setVideoDuration] = useState<string | undefined>(undefined);
  const isVideoGenerationFailed =
    !displayedVideoUrl &&
    Boolean(attrs?.generativeTaskId) &&
    Boolean(effectiveGenerativeTask) &&
    [GenerativeTaskStatusEnum.Failed, GenerativeTaskStatusEnum.Cancelled].includes(
      effectiveGenerativeTask!.status,
    );
  const currentRenderedVideoUrl = displayedVideoUrl;
  currentRenderedVideoUrlRef.current = currentRenderedVideoUrl;
  const isActivelyRenderingVideo = isCreatingVideo || isRenderingVideo || isResumedRendering;
  const hasNewRenderedVideo =
    Boolean(generatedVideoUrl || resumedVideoUrl) ||
    Boolean(currentRenderedVideoUrl && currentRenderedVideoUrl !== renderStartVideoUrl);

  // Keep showing the generation loader while a fresh render is in flight, even if an older
  // video is still present on the block. Stop once the current render yields a new URL.
  const effectiveIsRendering = isActivelyRenderingVideo && !hasNewRenderedVideo;

  useEffect(() => {
    if (isActivelyRenderingVideo && renderStartVideoUrl === null) {
      setRenderStartVideoUrl(currentRenderedVideoUrlRef.current);
      return;
    }

    if (!effectiveIsRendering && renderStartVideoUrl !== null) {
      setRenderStartVideoUrl(null);
    }
  }, [effectiveIsRendering, isActivelyRenderingVideo, renderStartVideoUrl]);

  useEffect(() => {
    if (!displayedVideoUrl) {
      setVideoDuration(undefined);
      return;
    }

    let cancelled = false;
    const videoEl = document.createElement('video');
    videoEl.preload = 'metadata';
    videoEl.src = displayedVideoUrl;

    const handleLoadedMetadata = () => {
      if (cancelled) return;
      const formatted = formatVideoDuration(videoEl.duration);
      setVideoDuration(formatted ?? undefined);
    };

    const handleError = () => {
      if (cancelled) return;
      setVideoDuration(undefined);
    };

    videoEl.addEventListener('loadedmetadata', handleLoadedMetadata);
    videoEl.addEventListener('error', handleError);

    return () => {
      cancelled = true;
      videoEl.removeEventListener('loadedmetadata', handleLoadedMetadata);
      videoEl.removeEventListener('error', handleError);
      videoEl.src = '';
      videoEl.load();
    };
  }, [displayedVideoUrl]);

  const hasVideoScript = videoScript.trim().length > 0;
  const hasStoryboard = storyboardSlides.length > 0;
  const hasRenderedVideo = Boolean(displayedVideoUrl);
  const isStoryboardGenerating = isSelectingSlides || isPreparingStoryboard;
  const aiVideoUiState = useMemo<GenerateVideoBannerState>(() => {
    if (effectiveIsRendering) {
      return 'video_rendering';
    }

    if (isVideoGenerationFailed) {
      return 'video_failed';
    }

    if (hasRenderedVideo) {
      return 'video_ready';
    }

    if (isStoryboardGenerating) {
      return 'storyboard_generating';
    }

    if (isGeneratingVideoScript) {
      return 'script_generating';
    }

    if (hasStoryboard) {
      return 'storyboard_ready';
    }

    if (hasVideoScript) {
      return 'script_ready';
    }

    return 'fresh_empty';
  }, [
    effectiveIsRendering,
    isVideoGenerationFailed,
    hasRenderedVideo,
    hasStoryboard,
    hasVideoScript,
    isGeneratingVideoScript,
    isStoryboardGenerating,
  ]);
  const isFreshBlockForAutoOpen = useMemo(
    () =>
      !attrs?.src &&
      !attrs?.generativeTaskId &&
      !hasVideoScript &&
      !hasStoryboard &&
      !effectiveIsRendering,
    [attrs?.generativeTaskId, attrs?.src, effectiveIsRendering, hasStoryboard, hasVideoScript],
  );

  const handleCreateVideoFromBanner = useEvent(() => {
    if (aiVideoUiState === 'fresh_empty') {
      openScriptModal(false);
      return;
    }

    if (aiVideoUiState === 'script_generating' || aiVideoUiState === 'script_ready') {
      openScriptModal(false);
      return;
    }

    openStoryboardModal();
  });

  // Auto-open the script modal once when a truly fresh block is first inserted.
  useEffect(() => {
    if (hasAutoOpenedRef.current) return;
    if (!editable) return;
    if (attrs?.hasInitialized) return;

    hasAutoOpenedRef.current = true;
    updateAttribute({ hasInitialized: true });

    if (isFreshBlockForAutoOpen) {
      handleCreateVideoFromBanner();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attrs?.hasInitialized, editable, isFreshBlockForAutoOpen, updateAttribute]);

  const initialConfigValues = useMemo<Partial<GenerateVideoFormValues> | undefined>(() => {
    return {
      avatar: videoAvatarId,
      videoType,
      voice: videoVoiceId,
    };
  }, [videoAvatarId, videoType, videoVoiceId]);

  const handleUpdateStoryboardTranscript = useCallback(
    (slideIndex: number, transcript: string) => {
      onChangeStoryboardSlides(
        storyboardSlides.map((s, i) => (i === slideIndex ? { ...s, transcript } : s)),
      );
    },
    [onChangeStoryboardSlides, storyboardSlides],
  );

  const handleUpdateStoryboardVariable = useCallback(
    (index: number, key: string, value: string) => {
      onChangeStoryboardSlides(
        storyboardSlides.map((s, i) =>
          i === index ? { ...s, variables: { ...(s.variables ?? {}), [key]: value } } : s,
        ),
      );
    },
    [onChangeStoryboardSlides, storyboardSlides],
  );

  const handleAddStoryboardSlide = useEvent(
    (slideTemplateId: string, template: VideoSlideTemplate) => {
      const normalizedSlideTemplateId = slideTemplateId.trim();
      if (!normalizedSlideTemplateId) {
        return;
      }

      const newSlide = createStoryboardSlideFromTemplate(template, {
        variables: getDefaultVariablesFromSchema(template.variableSchema),
      });

      const nextSlides = [...storyboardSlides, newSlide];
      onChangeStoryboardSlides(nextSlides);
      setSelectedStoryboardSlideIndex(nextSlides.length - 1);
    },
  );

  const handleDeleteStoryboardSlide = useCallback(
    (slideIndex: number) => {
      if (storyboardSlides.length <= 1) {
        showErrorSnackbar({
          message: 'Cannot delete the last slide.',
        });
        return;
      }

      if (!storyboardSlides[slideIndex]) {
        return;
      }

      const nextSlides = storyboardSlides.filter((_, index) => index !== slideIndex);
      onChangeStoryboardSlides(nextSlides);
      setSwitchingStoryboardSlideIndex((prevIndex) => {
        if (prevIndex === null) {
          return null;
        }
        if (prevIndex === slideIndex) {
          return null;
        }
        if (prevIndex > slideIndex) {
          return prevIndex - 1;
        }
        return prevIndex;
      });

      if (nextSlides.length === 0) {
        setSelectedStoryboardSlideIndex(0);
        return;
      }

      setSelectedStoryboardSlideIndex((prevIndex) => {
        if (prevIndex > slideIndex) {
          return prevIndex - 1;
        }
        if (prevIndex === slideIndex) {
          return Math.max(0, slideIndex - 1);
        }
        return Math.min(prevIndex, nextSlides.length - 1);
      });
    },
    [onChangeStoryboardSlides, showErrorSnackbar, storyboardSlides],
  );

  const handleChangeStoryboardSlideTemplate = useEvent(
    (slideIndex: number, slideTemplateId: string, template: VideoSlideTemplate) => {
      const normalizedSlideTemplateId = slideTemplateId.trim();
      if (!normalizedSlideTemplateId) {
        return;
      }

      const currentSlide = storyboardSlides[slideIndex];
      if (!currentSlide) {
        return;
      }

      if (currentSlide.slideTemplateId === normalizedSlideTemplateId) {
        return;
      }

      // For PPT/document-image mode, do not call switch template task.
      // Keep existing transcript/variables and only switch template identifiers locally.
      const isDocumentImageMode = Boolean(currentSlide.variables?.image?.trim());
      if (isDocumentImageMode) {
        const locallySwitchedSlide: StoryboardSlide = {
          ...currentSlide,
          slideTemplateId: normalizedSlideTemplateId,
          heygenTemplateId: template.heygen_template_id,
          name: template.name,
          previewUrl: template.preview_url ?? undefined,
          variableSchema: template.variableSchema ?? undefined,
        };

        onChangeStoryboardSlides(
          storyboardSlides.map((slide, index) =>
            index === slideIndex ? locallySwitchedSlide : slide,
          ),
        );
        return;
      }

      setSwitchingStoryboardSlideIndex(slideIndex);
      switchVideoSlideTemplate(
        currentSlide,
        normalizedSlideTemplateId,
        slideIndex,
        getLessonContent(),
        undefined,
        template,
      );
    },
  );

  const handleGenerateVideoFromStoryboard = useEvent(() => {
    generateVideo({
      avatarId: videoType === 'voiceOver' ? '' : videoAvatarId,
      voiceId: videoVoiceId,
      blockId: attrs?.id,
      slides: storyboardSlides.map((slide) => ({
        slideTemplateId: slide.slideTemplateId,
        heygenTemplateId: slide.heygenTemplateId,
        name: slide.name,
        transcript: slide.transcript,
        variables: slide.variables,
      })),
    });
  });

  return (
    <>
      <AsyncView
        fetching={effectiveIsRendering}
        isEmpty={!displayedVideoUrl && editable}
        renderData={() => (
          <GeneratedVideo
            captionUrl={displayedCaptionUrl || undefined}
            duration={videoDuration}
            editable={editable}
            videoUrl={displayedVideoUrl!}
            onEditVideoClick={openStoryboardModal}
          />
        )}
        renderEmptyState={() =>
          editable ? (
            <GenerateVideoBanner
              state={aiVideoUiState}
              onCreateVideoClick={handleCreateVideoFromBanner}
            />
          ) : (
            <></>
          )
        }
        renderLoader={() =>
          editable ? (
            <GeneratingVideo
              completedCount={effectiveCompletedCount}
              progress={effectiveVideoGenerationProgress}
              thumbnailUrl={
                storyboardSlides[0]?.variables?.image?.trim() || storyboardSlides[0]?.previewUrl
              }
              totalCount={effectiveTotalCount}
            />
          ) : (
            <></>
          )
        }
      />
      {isScriptModalOpen ? (
        <AsyncView
          fetching={isFetchingInitialGenerativeTask}
          isEmpty={false}
          renderData={() => (
            <GenerateVideoModal
              blockId={attrs?.id}
              generatedVideoScript={generatedVideoScript}
              hasLessonContent={!!getLessonContent().trim()}
              initialConfigValues={initialConfigValues}
              isGeneratingVideoScript={isGeneratingVideoScript}
              isSelectingSlides={isSelectingSlides || isPreparingStoryboard}
              modalClassName="!w-[120rem] !max-h-[80rem] !min-h-[60rem]"
              modalClasses={{ paper: '!w-[120rem] !max-h-[80rem] !min-h-[60rem]' }}
              shouldAutoOpenScriptPrompt={shouldAutoOpenScriptPrompt}
              slideDeckFileName={slideDeckFileName}
              slideDeckUrl={slideDeckUrl}
              videoScript={videoScript}
              onChangeVideoScript={onChangeVideoScript}
              onClose={closeModal}
              onConfigChange={onChangeVideoConfig}
              onGenerateStoryboard={handleGenerateStoryboard}
              onGenerateVideoScript={generateVideoScript}
              onRegenerateVideoScript={regenerateVideoScript}
              onSlideDeckRemove={() => {
                updateAttribute({
                  slideDeckUrl: null,
                  slideDeckFileName: null,
                  slideDeckFileSummary: null,
                });
              }}
              onSlideDeckUpload={({ url, fileName }) => {
                updateAttribute({
                  slideDeckUrl: url,
                  slideDeckFileName: fileName,
                  slideDeckFileSummary: null,
                });
              }}
            />
          )}
          renderLoader={() => <></>}
        />
      ) : null}
      {isStoryboardModalOpen ? (
        <StoryboardModal
          avatarId={videoAvatarId}
          isGeneratingScript={isGeneratingVideoScript}
          isGeneratingVideo={isCreatingVideo}
          isGenerationDisabled={Boolean(
            isCreatingVideo ||
              effectiveIsRendering ||
              isSwitchingVideoSlideTemplate ||
              storyboardSlides.some(
                (slide) =>
                  !slide.transcript.trim() ||
                  (Object.values(slide.variables ?? {}) as string[]).some((v) => !v.trim()),
              ),
          )}
          isLoading={isSelectingSlides || isPreparingStoryboard}
          selectedSlideIndex={selectedStoryboardSlideIndex}
          slides={storyboardSlides}
          switchingSlideIndex={switchingStoryboardSlideIndex}
          voiceId={videoVoiceId}
          onAddSlide={handleAddStoryboardSlide}
          onApplySettings={(newAvatarId, newVoiceId) =>
            onChangeVideoConfig({
              avatarId: newAvatarId,
              voiceId: newVoiceId,
            })
          }
          onChangeSlideTemplate={handleChangeStoryboardSlideTemplate}
          onClose={closeModal}
          onDeleteSlide={handleDeleteStoryboardSlide}
          onEditScriptAndStyle={openScriptModal}
          onGenerateVideo={handleGenerateVideoFromStoryboard}
          onSelectSlide={setSelectedStoryboardSlideIndex}
          onUpdateTranscript={handleUpdateStoryboardTranscript}
          onUpdateVariable={handleUpdateStoryboardVariable}
        />
      ) : null}
    </>
  );
};

export default GenerateVideo;
